import { useState, useEffect } from 'react';
import { formsAPI } from '../lib/api';
import { cn, formatDateTime, getCategoryColor } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Search,
  Filter,
  FileText,
  Calendar,
  User,
  Loader2,
  Eye,
  X,
} from 'lucide-react';
import { toast } from 'sonner';

export default function SubmissionsPage() {
  const [submissions, setSubmissions] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    form_template_id: '',
    date_from: '',
    date_to: '',
  });
  const [showFilters, setShowFilters] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [submissionsRes, templatesRes] = await Promise.all([
        formsAPI.getSubmissions({}),
        formsAPI.getTemplates({}),
      ]);
      setSubmissions(submissionsRes.data);
      setTemplates(templatesRes.data);
    } catch (err) {
      toast.error('Failed to fetch submissions');
    }
    setLoading(false);
  };

  const applyFilters = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.form_template_id) params.form_template_id = filters.form_template_id;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      
      const response = await formsAPI.getSubmissions(params);
      setSubmissions(response.data);
    } catch (err) {
      toast.error('Failed to fetch submissions');
    }
    setLoading(false);
  };

  const clearFilters = () => {
    setFilters({ form_template_id: '', date_from: '', date_to: '' });
    fetchData();
  };

  const hasActiveFilters = filters.form_template_id || filters.date_from || filters.date_to;

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="submissions-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Submissions</h1>
          <p className="text-muted-foreground mt-1">
            View and manage form submissions
          </p>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <Button
              variant={showFilters ? 'secondary' : 'outline'}
              onClick={() => setShowFilters(!showFilters)}
              className="gap-2"
              data-testid="filter-toggle"
            >
              <Filter className="h-4 w-4" />
              Filters
              {hasActiveFilters && (
                <Badge variant="secondary" className="ml-1">Active</Badge>
              )}
            </Button>
          </div>

          {showFilters && (
            <div className="mt-4 pt-4 border-t flex flex-wrap gap-4 items-end animate-fadeIn">
              <div className="space-y-2">
                <label className="text-sm font-medium">Form Type</label>
                <Select
                  value={filters.form_template_id || "all"}
                  onValueChange={(v) => setFilters(prev => ({ ...prev, form_template_id: v === "all" ? "" : v }))}
                >
                  <SelectTrigger className="w-[200px]" data-testid="form-type-filter">
                    <SelectValue placeholder="All forms" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All forms</SelectItem>
                    {templates.map(t => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">From Date</label>
                <Input
                  type="date"
                  value={filters.date_from}
                  onChange={(e) => setFilters(prev => ({ ...prev, date_from: e.target.value }))}
                  className="w-[160px]"
                  data-testid="date-from-filter"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">To Date</label>
                <Input
                  type="date"
                  value={filters.date_to}
                  onChange={(e) => setFilters(prev => ({ ...prev, date_to: e.target.value }))}
                  className="w-[160px]"
                  data-testid="date-to-filter"
                />
              </div>

              <Button onClick={applyFilters} data-testid="apply-filters-btn">Apply</Button>
              
              {hasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters} className="gap-1">
                  <X className="h-4 w-4" />
                  Clear
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Results */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">
            {loading ? 'Loading...' : `${submissions.length} Submission${submissions.length !== 1 ? 's' : ''}`}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : submissions.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-20" />
              <h3 className="text-lg font-medium mb-1">No submissions found</h3>
              <p className="text-muted-foreground">
                {hasActiveFilters ? 'Try adjusting your filters' : 'Submissions will appear here'}
              </p>
            </div>
          ) : (
            <ScrollArea className="h-[500px]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Form</TableHead>
                    <TableHead className="hidden sm:table-cell">Category</TableHead>
                    <TableHead className="hidden md:table-cell">Submitted By</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="w-[80px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {submissions.map((submission) => (
                    <TableRow key={submission.id} data-testid={`submission-row-${submission.id}`}>
                      <TableCell>
                        <div className="font-medium">{submission.form_name}</div>
                      </TableCell>
                      <TableCell className="hidden sm:table-cell">
                        <Badge 
                          variant="secondary" 
                          className={cn('text-xs', getCategoryColor(submission.category || 'general'))}
                        >
                          {submission.category || 'general'}
                        </Badge>
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-muted-foreground" />
                          <span>{submission.submitted_by_name}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 text-sm">
                          <Calendar className="h-4 w-4 text-muted-foreground" />
                          {formatDateTime(submission.created_at)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setSelectedSubmission(submission)}
                          data-testid={`view-submission-${submission.id}`}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollArea>
          )}
        </CardContent>
      </Card>

      {/* View Submission Dialog */}
      <Dialog open={!!selectedSubmission} onOpenChange={() => setSelectedSubmission(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{selectedSubmission?.form_name}</DialogTitle>
            <DialogDescription>
              Submitted by {selectedSubmission?.submitted_by_name} on{' '}
              {formatDateTime(selectedSubmission?.created_at)}
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="max-h-[60vh]">
            <div className="space-y-4 p-1">
              {selectedSubmission?.data && Object.entries(selectedSubmission.data).map(([key, value]) => (
                <div key={key} className="p-3 bg-muted rounded-lg">
                  <p className="text-sm text-muted-foreground capitalize mb-1">
                    {key.replace(/_/g, ' ')}
                  </p>
                  <p className="font-medium">
                    {typeof value === 'boolean' 
                      ? (value ? 'Yes' : 'No') 
                      : (value || '—')}
                  </p>
                </div>
              ))}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
}
